import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { PAYMENT_PROVIDERS_NAMES } from 'domain/paySettings/helpers';

import ToggleCell from './components/ToggleCell';
import DisconnectCell from './components/DisconnectCell';
import DetailsCell from './components/DetailsCell';
import EditCell from './components/EditCell';

interface Account {
  id: string;
  status: string;
  isSuspended: boolean;
  [key: string]: unknown;
}

interface UseAccountListColumnsParams {
  handleSuspend: (params: { id: string; isSuspended: boolean }) => void;
  handleDisconnect: (id: string) => void;
  providerName: string;
  handleOpenDetailsModal: (account: unknown) => void;
  handleOpenEditModal: (account: unknown) => void;
}

const checkDisabled = (account: Account) => account.status === 'CLOSED';

export const useAccountListColumns = ({
  handleSuspend,
  handleDisconnect,
  providerName,
  handleOpenDetailsModal,
  handleOpenEditModal,
}: UseAccountListColumnsParams) => {
  const { formatMessage } = useIntl();

  const renderSuspendCell = useCallback(
    (account: Account) => (
      <ToggleCell
        id={account.id}
        value={account.isSuspended}
        onClick={handleSuspend}
        disabled={checkDisabled(account)}
      />
    ),
    [handleSuspend],
  );

  const renderDisconnectCell = useCallback(
    (account: Account) => <DisconnectCell id={account.id} onClick={handleDisconnect} disabled={checkDisabled(account)} />,
    [handleDisconnect],
  );

  const renderEditCell = useCallback(
    (account: Account) => <EditCell value={account} onClick={handleOpenEditModal} disabled={checkDisabled(account)} />,
    [handleOpenEditModal],
  );

  const renderDetailsCell = useCallback(
    (account: Account) => <DetailsCell value={account} onClick={handleOpenDetailsModal} disabled={checkDisabled(account)} />,
    [handleOpenDetailsModal],
  );

  return useMemo(() => {
    const columns = [
      {
        id: 'currencyCode',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.currency',
          defaultMessage: 'Currency',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.airwallex, PAYMENT_PROVIDERS_NAMES.checkbook],
        searchable: true,
      },
      {
        id: 'name',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.accountName',
          defaultMessage: 'Account Name',
        }),
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: true,
      },
      {
        id: 'details',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.details',
          defaultMessage: 'Details',
        }),
        component: renderDetailsCell,
        enableFor: [PAYMENT_PROVIDERS_NAMES.checkbook],
        searchable: false,
      },
      {
        id: 'iban',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.iban',
          defaultMessage: 'Account Number / IBAN',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.airwallex],
        searchable: true,
      },
      {
        id: 'accountNumber',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.accountNumber',
          defaultMessage: 'Account Number',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.openFinance],
        searchable: true,
      },
      {
        id: 'providerId',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.bank',
          defaultMessage: 'Bank',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.openFinance],
        searchable: true,
      },
      {
        id: 'psuId',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.psuId',
          defaultMessage: 'PSU ID',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.openFinance],
        searchable: true,
      },
      {
        id: 'swift',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.swift',
          defaultMessage: 'SWIFT / Routing Number',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.airwallex],
        searchable: true,
      },
      {
        id: 'countryCode',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.country',
          defaultMessage: 'Country',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.airwallex],
        searchable: true,
      },
      {
        id: 'erpBankAccountName',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.bankAccount',
          defaultMessage: '#GL account of the Bank Account',
        }),
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: true,
      },
      {
        id: 'status',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.status',
          defaultMessage: 'Status',
        }),
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: true,
      },
      {
        id: 'balance',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.balance',
          defaultMessage: 'Balance',
        }),
        enableFor: [PAYMENT_PROVIDERS_NAMES.checkbook],
        searchable: true,
      },
      {
        id: 'isSuspend',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.suspendAccount',
          defaultMessage: 'Suspend Account',
        }),
        component: renderSuspendCell,
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: false,
      },
      {
        id: 'delete',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.deleteAccount',
          defaultMessage: 'Delete Account',
        }),
        component: renderDisconnectCell,
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: false,
      },
      {
        id: 'edit',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.editAccount',
          defaultMessage: 'Edit Account',
        }),
        component: renderEditCell,
        enableFor: [
          PAYMENT_PROVIDERS_NAMES.airwallex,
          PAYMENT_PROVIDERS_NAMES.checkbook,
          PAYMENT_PROVIDERS_NAMES.openFinance,
        ],
        searchable: false,
      },
    ];

    return columns.filter(({ enableFor }) => (enableFor as string[]).includes(providerName));
  }, [formatMessage, renderDetailsCell, renderSuspendCell, renderDisconnectCell, renderEditCell, providerName]);
};
